// import packages
import React from 'react'
import PropTypes from 'prop-types'
import { TransitionGroup, CSSTransition } from 'react-transition-group'
import { useHistory } from 'react-router-dom'

// import apis
import { apiDeleteCard, apiLeaveCard } from 'api/cards'
import { apiAddPrintRequest, apiDeletePrintRequest } from 'api/print_requests'

// import redux, selectors, actions
import { useDispatch, useSelector } from 'react-redux'
import { deleteCard } from 'redux/actions/cards'
import { addPrintRequest, deletePrintRequest } from 'redux/actions/print_requests'

// import contexts
import { AuthContext } from 'contexts/AuthContext'
import { FetchContext } from 'contexts/FetchContext'

// import hooks

// import utilities
import { safelyGet, toSnakeCase, toTitleCase } from 'helpers'

// import common elements
import { Button, ButtonToolbar, Accordion, Alert } from 'react-bootstrap'
import { GetPdfButton } from 'components/elements/Pdf'
import { Confirm } from 'components/elements/Misc'

// import components
import CreateCard from './CreateCard'
import ShareOptions from './ShareOptions'

// import styles
import { CardRole } from './styles'

// local constants
const cleanFileName = title =>
  toSnakeCase(title.replace(/[^a-zA-Z0-9_-]+/g, ''))
const printRequestStatus = status => {
  switch (status) {
    case 'pending':
      return 'Pending'
    case 'downloaded':
      return 'Downloaded'
    case 'ready':
      return 'Ready for Pickup'
    case 'denied':
      return 'Denied'
    case 'complete':
      return 'Complete'
    default:
      return toTitleCase(status)
  }
}

// --------------------

const CardDetail = props => {
  const { card, printRequestQueue, printRequestQueueActive } = props

  const { getCurrentUserId, hasEditingRights } = React.useContext(AuthContext)

  const currentUserId = getCurrentUserId()

  const allPrintRequests = useSelector(state => state.print_requests)
  const printRequests = Object.values(allPrintRequests).filter(
    pr => pr.card_id === card.id && pr.user_id === currentUserId
  )
  const printRequestsCount = printRequests.length
  const waitingPrintRequests = printRequests.filter(pr => pr.status === 'pending' || pr.status === 'downloaded')
  const readyForPickupCount = printRequests.filter(pr => pr.status === 'ready').length
  const reduxDispatch = useDispatch()
  const { authAxios } = React.useContext(FetchContext)
  const history = useHistory()


  const timestamp = new Date(card.updated_at)

  const [showDetail, setShowDetail] = React.useState(null)

  const canEditCard = hasEditingRights(card)

  const canCloneCard = true

  const canShareCard = !card.is_public && canEditCard

  const canLeaveCard = !card.is_public && card.creator_id !== currentUserId

  const canDeleteCard = card.creator_id === currentUserId

  let names = safelyGet(card, ['settings', 'names', 'names'])
  if (names && (names[0] === '\\' || names[0] === '/')) {
    names = names.substr(1)
  }
  const generalApproach = safelyGet(card, [
    'settings',
    'overview',
    'general_appoach',
  ])

  const toggleDetail = show => setShowDetail(d => (d === show ? null : show))

  const toggleShareOptions = () => toggleDetail('share')

  const toggleCloneOptions = () => toggleDetail('clone')

  const toggleDelete = () => toggleDetail('delete')

  const toggleLeave = () => toggleDetail('leave')

  const cardRole = card.is_public ? (
    <CardRole public>Public Card</CardRole>
  ) : card.creator_id === currentUserId ? (
    <CardRole creator>Creator</CardRole>
  ) : card.editors.includes(currentUserId) ? (
    <CardRole editor>Editor</CardRole>
  ) : (
    <CardRole>Read Only</CardRole>
  )

  const editCard = () => {
    window.scrollTo(0, 0)
    history.push(`/edit-card/${card.id}`)
  }

  const viewCard = () => {
    window.scrollTo(0, 0)
    history.push(`/card/${card.id}`)
  }



  const exportConfiguration = () => {
    const fileName = cleanFileName(card.title) + '_' + new Date().toISOString()
    const blob = new Blob(
      [JSON.stringify({ settings: card.settings, notes: card.notes })],
      { type: 'text/plain' },
    )
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    // link.download = `${filename}.json`
    link.download = `${fileName}.json`
    link.href = url
    link.click()
  }

  const reallyDelete = () => {
    apiDeleteCard(authAxios, card.id, {
      successDispatch: [reduxDispatch, deleteCard],
      successMsg: 'Card deleted',
    })
  }

  const reallyLeave = () => {
    apiLeaveCard(authAxios, card.id, {
      successDispatch: [reduxDispatch, deleteCard],
      successMsg: 'Card removed',
    })
  }

  const toggle = () => props.openCard(card.id)

  const createPrintRequest = () => {
    if (printRequestQueue) {
      apiAddPrintRequest(authAxios, card.id, printRequestQueue, {
        successDispatch: [reduxDispatch, addPrintRequest],
        successMsg: 'Print request created',
        errorMsg: 'Failed to create print request',
      })
    } else {
      alert('Sorry, no print request queue configured.')
    }
  }

  const cancelPrintRequest = (printRequestId) => {
    apiDeletePrintRequest(authAxios, printRequestId, {
      successDispatch: [reduxDispatch, deletePrintRequest],
      successMsg: 'Print request(s) cancelled',
      errorMsg: 'Failed to cancel print request',
    })
  }

  return (
    <Accordion.Item eventKey={card.id} className='mt-2'>
      <Accordion.Header onClick={toggle}>
        <div className='w-100 d-flex justify-content-between align-items-baseline'>
          <div>
            {card.title}{printRequestQueueActive && waitingPrintRequests.length > 0 && (
              <span className='badge bg-warning ms-2'>
                {waitingPrintRequests.length} pending print request{waitingPrintRequests.length > 1 ? 's' : ''}
              </span>
            )}
            {readyForPickupCount > 0 && (
              <span className='badge bg-success ms-2'>
                {readyForPickupCount} ready for pickup{readyForPickupCount > 1 ? 's' : ''}
              </span>
            )}
            {card.creator_id !== currentUserId && (
              <div className='text-muted small mt-1 force-base-font'>
                Created by {card.creator_name}
              </div>
            )}
          </div>
          <div className='pe-3'>{cardRole}</div>
        </div>
      </Accordion.Header>

      <Accordion.Body className='bg-light pt-0 px-0'>
        <div className='bg-white p-2 rounded-bottom-3 border border-success border-2'>
          {printRequestQueueActive && (
            <div style={{ display: 'grid', gridTemplateColumns: 'max-content max-content max-content', columnGap: '10px', borderBottom: '1px solid #ccc', paddingBottom: '5px', marginBottom: '5px' }}>
              {printRequests.map(pr => <div style={{ display: 'contents' }} key={pr.id}>
                <div><strong>{printRequestStatus(pr.status)}</strong></div>
                <div>
                  Print Request created at {new Date(pr.created_at).toLocaleTimeString()}.
                </div>
                <div>
                  {pr.status === 'denied' && pr.denial_comment}
                  {pr.status === 'pending' && (
                    <Button size='sm' variant='outline-secondary' onClick={() => cancelPrintRequest(pr.id)}>
                      Cancel Print Request
                    </Button>
                  )}
                </div>
              </div>)}
            </div>
          )}
          {printRequestQueue && printRequestsCount < 9 && (
              <div className='mb-2'><Button
                size='sm'
                variant='primary'
                onClick={createPrintRequest}
              >
                Please print my card at {printRequestQueue}!
              </Button></div>
            )}
          <div>
            {names && <p>Names on card: {names}</p>}
            {generalApproach && <p>General Approach: {generalApproach}</p>}
            {card.latest_editor && (
              <p>
                <small>
                  Last edited by {card.latest_editor} on{' '}
                  {timestamp.toLocaleDateString()},{' '}
                  {timestamp.toLocaleTimeString()}.
                </small>
              </p>
            )}
          </div>

          <div className='d-sm-block d-md-flex justify-content-between'>
            <ButtonToolbar>
              <Button size='sm' variant='outline-primary' onClick={viewCard}>
                View
              </Button>
              {canEditCard && (
                <Button size='sm' variant='outline-primary' onClick={editCard}>
                  Edit
                </Button>
              )}

              <GetPdfButton cardId={card.id} />

              <Button
                size='sm'
                variant='outline-primary'
                onClick={exportConfiguration}
              >
                Export Config
              </Button>
              
            </ButtonToolbar>
            <ButtonToolbar className='mt-3 mt-md-0'>
              {canShareCard && (
                <Button
                  size='sm'
                  variant={
                    showDetail === 'share' ? 'success' : 'outline-success'
                  }
                  onClick={toggleShareOptions}
                >
                  {showDetail === 'share' ? 'Close Sharing' : 'Sharing ...'}
                </Button>
              )}
              {canCloneCard && (
                <Button
                  size='sm'
                  variant={
                    showDetail === 'clone' ? 'success' : 'outline-success'
                  }
                  onClick={toggleCloneOptions}
                >
                  {showDetail === 'clone' ? 'Close Duplicate' : 'Duplicate ...'}
                </Button>
              )}
              {canLeaveCard && (
                <Button
                  size='sm'
                  variant={showDetail === 'leave' ? 'danger' : 'outline-danger'}
                  onClick={toggleLeave}
                >
                  {showDetail === 'leave' ? 'Cancel Leave' : 'Leave ...'}
                </Button>
              )}
              {canDeleteCard && (
                <Button
                  size='sm'
                  variant={
                    showDetail === 'delete' ? 'danger' : 'outline-danger'
                  }
                  onClick={toggleDelete}
                >
                  {showDetail === 'delete' ? 'Cancel Delete' : 'Delete ...'}
                </Button>
              )}
            </ButtonToolbar>
          </div>

          <TransitionGroup>
            <CSSTransition
              key={showDetail || 'nothing'}
              classNames='fade-on-change'
              exit={false}
              timeout={{ enter: 500, exit: 0 }}
            >
              {showDetail === 'leave' ? (
                <Alert variant='danger'>
                  <Confirm
                    content='Are you sure you want to leave this card? '
                    open={showDetail === 'leave'}
                    confirmButton='Yes, I want to leave'
                    onConfirm={reallyLeave}
                    cancelButton='Nope, changed my mind'
                    onCancel={toggleLeave}
                  />
                </Alert>
              ) : showDetail === 'delete' ? (
                <Alert variant='danger'>
                  <Confirm
                    content='Are you sure you want to delete this card? It cannot be restored.'
                    open={showDetail === 'delete'}
                    confirmButton='Yes, please delete'
                    onConfirm={reallyDelete}
                    cancelButton='Nope, changed my mind'
                    onCancel={toggleDelete}
                  />
                </Alert>
              ) : showDetail === 'clone' ? (
                <CreateCard
                  fromId={card.id}
                  openCard={props.openCard}
                  closeAdd={() => setShowDetail(null)}
                />
              ) : showDetail === 'share' ? (
                <ShareOptions card={card} />
              ) : (
                <div></div>
              )}
            </CSSTransition>
          </TransitionGroup>
        </div>
      </Accordion.Body>
    </Accordion.Item>
  )
}

CardDetail.propTypes = {
  card: PropTypes.object.isRequired,
  openCard: PropTypes.func.isRequired,
  printRequestQueue: PropTypes.string,
  printRequestQueueActive: PropTypes.bool.isRequired,
}

export default CardDetail
